import React, { useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CompanyOnboardingContext } from 'contexts/CompanyOnboardingContext';
import { SessionContext } from 'contexts/SessionContext';
import { COMPANY_ONBOARDING_REQUIRED } from 'components/ui/utils/constant';
import { HiX, HiCheckCircle, HiSparkles } from 'react-icons/hi';
import { BsStars } from 'react-icons/bs';
import useDarkMode from 'utils/hooks/useDarkMode';
import CompanyOnboardingModalFooter from './CompanyOnboardingModalFooter';
import { useWhitelabel } from 'contexts/WhitelabelThemeContext';

const CompanyOnboardingModal = () => {
  const { 
    close, 
    steps,
    currentStep, 
    goToStep,
    isOpened 
  } = useContext(CompanyOnboardingContext);
  const { user } = useContext(SessionContext);
  const { whitelabel } = useWhitelabel();
  const [isDarkMode] = useDarkMode();

  if (!isOpened) return null;
  if (!user?.id) return null;

  const isClosable = !COMPANY_ONBOARDING_REQUIRED || user?.companies?.length > 1 || !!user?.subscription;
  const currentStepObj = steps[currentStep];

  return (
    <AnimatePresence>
      {isOpened && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          onClick={isClosable ? close : undefined}
        >
          {/* Backdrop with blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/80 backdrop-blur-xl"
          />

          {/* Modal Content */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            className={`relative w-full max-w-7xl h-[95vh] md:h-auto md:max-h-[95vh] rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl ${
              isDarkMode 
                ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' 
                : 'bg-gradient-to-br from-gray-50 via-white to-gray-50'
            }`}
          >
            {/* Close Button */}
            {isClosable && (
              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={close}
                className={`absolute top-3 right-3 md:top-6 md:right-6 z-50 p-2 md:p-3 backdrop-blur-xl rounded-full shadow-2xl transition-colors ${
                  isDarkMode
                    ? 'bg-white/10 hover:bg-white/20 text-white border border-white/20'
                    : 'bg-gray-900/10 hover:bg-gray-900/20 text-gray-900 border border-gray-900/20'
                }`}
              >
                <HiX className="text-xl md:text-2xl" />
              </motion.button>
            )}

            {/* Content Layout */}
            <div className="flex flex-col h-full">
              {/* Mobile/Tablet Header - Horizontal Step Indicator */}
              <div className={`lg:hidden border-b ${
                isDarkMode ? 'border-white/10 bg-gray-900/50' : 'border-gray-200 bg-white/50'
              } backdrop-blur-xl`}>
                <div className="px-4 py-4">
                  {/* Mobile Title */}
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-3 mb-4"
                  >
                    <motion.div
                      className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/30 shrink-0"
                      animate={{ rotate: [0, 5, -5, 0] }}
                      transition={{ duration: 4, repeat: Infinity }}
                    >
                      <BsStars className="text-xl text-white" />
                    </motion.div>
                    <div className="flex-1 min-w-0">
                      <h1 className={`!text-lg md:!text-xl font-black truncate ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        Welcome to {whitelabel?.name}!
                      </h1>
                      <p className={`text-xs ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        Step {currentStep + 1} of {steps.length}
                      </p>
                    </div>
                  </motion.div>

                  {/* Horizontal Step Icons */}
                  <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
                    {steps.map((step, index) => {
                      const isCompleted = index < currentStep;
                      const isCurrent = index === currentStep;
                      
                      return (
                        <React.Fragment key={step.id}>
                          <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: index * 0.05 }}
                            onClick={() => goToStep(step.id)}
                            className={` w-10 h-10 md:w-12 md:h-12 cursor-pointer shrink-0 ${
                              isCurrent ? 'scale-110' : ''
                            }`}
                          >
                            <div className={`relative w-10 h-10 md:w-12 md:h-12 rounded-xl flex-1 flex items-center justify-center transition-all ${
                              isCompleted
                                ? 'bg-gradient-to-br from-green-500 to-emerald-500 shadow-lg shadow-green-500/30'
                                : isCurrent
                                  ? 'bg-gradient-to-br from-purple-500 to-pink-500 shadow-lg shadow-purple-500/30'
                                  : isDarkMode
                                    ? 'bg-gray-700/50'
                                    : 'bg-gray-200'
                            }`}>
                              {isCompleted ? (
                                <HiCheckCircle className="text-white text-xl" />
                              ) : (
                                <span className={`text-lg md:text-xl ${isCurrent ? 'text-white' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {step.icon}
                                </span>
                              )}
                              {isCurrent && (
                                <motion.div
                                  className="absolute inset-0 rounded-xl border-2 border-purple-400"
                                  animate={{ scale: [1, 1.1, 1], opacity: [1, 0.5, 1] }}
                                  transition={{ duration: 2, repeat: Infinity }}
                                />
                              )}
                            </div>
                          </motion.div>
                          {index < steps.length - 1 && (
                            <div className={`h-0.5 w-full md:w-full ${
                              index < currentStep
                                ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                                : isDarkMode
                                  ? 'bg-gray-700'
                                  : 'bg-gray-300'
                            }`} />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Desktop/Tablet Landscape Layout */}
              <div className="flex flex-col lg:flex-row flex-1 overflow-hidden">
                {/* Left Side - Visual Progress & Animation (Desktop Only) */}
                <div className={`hidden lg:flex flex-[0.85] lg:w-2/5 p-6 xl:p-8 flex-col justify-between overflow-hidden relative ${
                  isDarkMode 
                    ? 'bg-gradient-to-br from-purple-900/30 via-pink-900/20 to-blue-900/30' 
                    : 'bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50'
                }`}>
                  {/* Animated Background Elements */}
                  <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <motion.div
                      animate={{
                        x: [0, 100, 0],
                        y: [0, 50, 0],
                        scale: [1, 1.2, 1],
                      }}
                      transition={{ duration: 20, repeat: Infinity }}
                      className="absolute -top-20 -left-20 w-96 h-96 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full blur-3xl"
                    />
                    <motion.div
                      animate={{
                        x: [0, -80, 0],
                        y: [0, 80, 0],
                        scale: [1, 1.3, 1],
                      }}
                      transition={{ duration: 25, repeat: Infinity }}
                      className="absolute -bottom-20 -right-20 w-96 h-96 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-full blur-3xl"
                    />
                  </div>

                  {/* Header with icon */}
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="relative z-10 flex items-center gap-4"
                  >
                    <motion.div
                      className="w-14 h-14 xl:w-16 xl:h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-2xl shadow-purple-500/50 shrink-0"
                      animate={{ rotate: [0, 5, -5, 0] }}
                      transition={{ duration: 4, repeat: Infinity }}
                    >
                      <BsStars className="text-2xl xl:text-3xl text-white" />
                    </motion.div>
                    <div className="flex-1 min-w-0">
                      <h1 className={`text-2xl xl:text-3xl 2xl:text-4xl font-black mb-2 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        Welcome to {whitelabel?.name}! 🎉
                      </h1>
                      <p className={`text-sm xl:text-base ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {isClosable 
                          ? "Set up your workspace in a few steps"
                          : "Let's set up your workspace"
                        }
                      </p>
                    </div>
                  </motion.div>

                  {/* Step Progress */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="relative z-10 space-y-3"
                  >
                    {steps.map((step, index) => {
                      const isCompleted = index < currentStep;
                      const isCurrent = index === currentStep;

                      return (
                        <motion.div
                          key={step.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.4 + index * 0.1 }}
                          onClick={() => goToStep(step.id)}
                          className={`flex items-center gap-3 xl:gap-4 p-3 xl:p-4 rounded-xl xl:rounded-2xl cursor-pointer transition-all ${
                            isCurrent
                              ? isDarkMode
                                ? 'bg-white/10 backdrop-blur-xl border-2 border-purple-500'
                                : 'bg-white/80 backdrop-blur-xl border-2 border-purple-500 shadow-lg'
                              : isCompleted
                                ? isDarkMode
                                  ? 'bg-white/5 backdrop-blur-xl hover:bg-white/10'
                                  : 'bg-white/50 backdrop-blur-xl hover:bg-white/70'
                                : isDarkMode
                                  ? 'bg-white/[0.02] backdrop-blur-xl opacity-60'
                                  : 'bg-white/30 backdrop-blur-xl opacity-60'
                          }`}
                        >
                          {/* Icon/Status */}
                          <div className={`w-10 h-10 xl:w-12 xl:h-12 rounded-lg xl:rounded-xl flex items-center justify-center shrink-0 ${
                            isCompleted
                              ? 'bg-gradient-to-br from-green-500 to-emerald-500'
                              : isCurrent
                                ? 'bg-gradient-to-br from-purple-500 to-pink-500'
                                : isDarkMode
                                  ? 'bg-gray-700'
                                  : 'bg-gray-200'
                          }`}>
                            {isCompleted ? (
                              <HiCheckCircle className="text-white text-xl xl:text-2xl" />
                            ) : (
                              <span className={`text-xl xl:text-2xl ${isCurrent ? 'text-white' : ''}`}>
                                {step.icon}
                              </span>
                            )}
                          </div>

                          {/* Step Info */}
                          <div className="flex-1 min-w-0">
                            <div className={`text-xs xl:text-sm font-bold mb-0.5 xl:mb-1 ${
                              isCurrent
                                ? isDarkMode ? 'text-white' : 'text-gray-900'
                                : isDarkMode ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                              Step {index + 1} of {steps.length}
                            </div>
                            <div className="flex items-center gap-2">
                              <div className={`text-sm xl:text-base font-semibold truncate ${
                                isCurrent
                                  ? isDarkMode ? 'text-white' : 'text-gray-900'
                                  : isDarkMode ? 'text-gray-300' : 'text-gray-700'
                              }`}>
                                {step.title}
                              </div>
                              {step.isOptional && (
                                <span className="text-[10px] xl:text-xs text-purple-500 font-semibold px-1.5 xl:px-2 py-0.5 bg-purple-500/10 rounded-full shrink-0">
                                  Optional
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Progress indicator */}
                          {isCurrent && (
                            <motion.div
                              animate={{ scale: [1, 1.2, 1] }}
                              transition={{ duration: 2, repeat: Infinity }}
                              className="w-2 h-2 rounded-full bg-purple-500 shrink-0"
                            />
                          )}
                        </motion.div>
                      );
                    })}
                  </motion.div>

                  {/* Decorative Image */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 0.3, scale: 1 }}
                    transition={{ delay: 0.6 }}
                    className="absolute bottom-0 right-0 w-48 h-48 xl:w-64 xl:h-64 pointer-events-none"
                  >
                    <img 
                      src="/img/others/social-networks-asset.png" 
                      alt="Decoration"
                      className="w-full h-full object-contain opacity-50"
                    />
                  </motion.div>
                </div>
            
                {/* Right Side - Form Content */}
                <div className="flex-1 lg:!w-3/5 bg-transparent flex flex-col overflow-hidden h-[95vh]">
                  {/* Scrollable Content Area */}
                  <div className="flex-1 overflow-y-auto px-4 py-6 md:px-6 md:py-8 lg:px-10 lg:py-10 xl:px-12 xl:py-12">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={currentStep}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                        className="flex flex-col"
                      >
                        {currentStepObj?.component || null}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                  
                  {/* Fixed Footer at Bottom */}
                  <CompanyOnboardingModalFooter />
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CompanyOnboardingModal;